/**
 * projections.ts - Utilidades de proyección para el visor cartográfico PTEL
 * 
 * Módulo dedicado a transformaciones de coordenadas para Andalucía.
 * Usa proj4js para conversiones precisas entre sistemas de referencia.
 * 
 * Sistema de referencia oficial: EPSG:25830 (UTM30 ETRS89)
 * Sistemas soportados: EPSG:25829, EPSG:25830, EPSG:25831, EPSG:4326
 * 
 * @module projections
 * @version 1.0.0 (V01)
 */

import proj4 from 'proj4';

// =============================================================================
// DEFINICIONES PROJ4 (strings constantes para registro y exportación)
// =============================================================================

/**
 * Strings de definición proj4 - almacenados como constantes para:
 * 1. Registro en proj4.defs()
 * 2. Exportación en PROJ4_DEFINITIONS para tests y validación
 */
const PROJ4_DEF_STRINGS = {
  'EPSG:25829': '+proj=utm +zone=29 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
  'EPSG:25830': '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
  'EPSG:25831': '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
  'EPSG:4326': '+proj=longlat +datum=WGS84 +no_defs +type=crs',
} as const;

// Registrar definiciones en proj4
// ETRS89 / UTM zone 29N (Galicia, Huelva occidental)
proj4.defs('EPSG:25829', PROJ4_DEF_STRINGS['EPSG:25829']);

// ETRS89 / UTM zone 30N (España central, mayor parte de Andalucía) - SISTEMA PRINCIPAL
proj4.defs('EPSG:25830', PROJ4_DEF_STRINGS['EPSG:25830']);

// ETRS89 / UTM zone 31N (Cataluña, Almería oriental)
proj4.defs('EPSG:25831', PROJ4_DEF_STRINGS['EPSG:25831']);

// WGS84 Geographic (GPS estándar)
proj4.defs('EPSG:4326', PROJ4_DEF_STRINGS['EPSG:4326']);

// =============================================================================
// TIPOS
// =============================================================================

/**
 * Coordenada en sistema UTM
 */
export interface UtmCoordinate {
  /** Coordenada Este (metros) */
  x: number;
  /** Coordenada Norte (metros) */
  y: number;
  /** Código EPSG del sistema UTM */
  epsg: EpsgCode;
  /** Zona UTM (29, 30, 31) */
  zone: UtmZone;
}

/**
 * Coordenada en WGS84 (grados decimales)
 */
export interface WgsCoordinate {
  /** Latitud (grados decimales, + = Norte) */
  lat: number;
  /** Longitud (grados decimales, + = Este) */
  lng: number;
}

/**
 * Zonas UTM válidas para España peninsular
 */
export type UtmZone = 29 | 30 | 31;

/**
 * Códigos EPSG soportados para UTM ETRS89
 */
export type EpsgCode = 'EPSG:25829' | 'EPSG:25830' | 'EPSG:25831' | 'EPSG:4326';

/**
 * Resultado de conversión con metadatos
 */
export interface ConversionResult<T> {
  /** Coordenada convertida */
  coordinate: T;
  /** Indica si la coordenada está dentro de Andalucía */
  isInAndalucia: boolean;
  /** Precisión estimada de la conversión (metros) */
  precision: number;
}

// =============================================================================
// CONSTANTES GEOGRÁFICAS
// =============================================================================

/**
 * Límites de longitud para zonas UTM en España
 * Zona 29: lng < -6°
 * Zona 30: -6° ≤ lng < 0°
 * Zona 31: lng ≥ 0°
 */
export const UTM_ZONE_BOUNDARIES = {
  ZONE_29_30: -6,  // Meridiano entre zona 29 y 30
  ZONE_30_31: 0,   // Meridiano entre zona 30 y 31
} as const;

/**
 * Bounding box aproximado de Andalucía (WGS84)
 */
export const ANDALUCIA_BOUNDS_WGS84 = {
  minLat: 35.9,   // Sur (Tarifa)
  maxLat: 38.75,  // Norte (Sierra Morena)
  minLng: -7.55,  // Oeste (Huelva)
  maxLng: -1.6,   // Este (Almería)
} as const;

/**
 * Bounding box aproximado de Andalucía (UTM30 ETRS89)
 */
export const ANDALUCIA_BOUNDS_UTM30 = {
  minX: 100000,   // Oeste (con margen)
  maxX: 620000,   // Este
  minY: 3975000,  // Sur
  maxY: 4290000,  // Norte
} as const;

// =============================================================================
// FUNCIONES PRINCIPALES
// =============================================================================

/**
 * Determina el código EPSG UTM basado en la longitud
 * 
 * Para España peninsular:
 * - Longitud < -6°: Zona 29 (EPSG:25829)
 * - -6° ≤ Longitud < 0°: Zona 30 (EPSG:25830)
 * - Longitud ≥ 0°: Zona 31 (EPSG:25831)
 * 
 * @param lng - Longitud en grados decimales (WGS84)
 * @returns Código EPSG correspondiente a la zona UTM
 * 
 * @example
 * getEpsgForLongitude(-5.5)  // 'EPSG:25830' (Sevilla)
 * getEpsgForLongitude(-7.0)  // 'EPSG:25829' (Huelva)
 * getEpsgForLongitude(-2.0)  // 'EPSG:25830' (Almería)
 */
export function getEpsgForLongitude(lng: number): EpsgCode {
  if (lng < UTM_ZONE_BOUNDARIES.ZONE_29_30) {
    return 'EPSG:25829';
  } else if (lng < UTM_ZONE_BOUNDARIES.ZONE_30_31) {
    return 'EPSG:25830';
  } else {
    return 'EPSG:25831';
  }
}

/**
 * Obtiene la zona UTM numérica basada en la longitud
 * 
 * @param lng - Longitud en grados decimales (WGS84)
 * @returns Número de zona UTM (29, 30, o 31)
 */
export function getUtmZoneForLongitude(lng: number): UtmZone {
  if (lng < UTM_ZONE_BOUNDARIES.ZONE_29_30) {
    return 29;
  } else if (lng < UTM_ZONE_BOUNDARIES.ZONE_30_31) {
    return 30;
  } else {
    return 31;
  }
}

/**
 * Convierte coordenadas WGS84 (lat/lng) a UTM ETRS89
 * 
 * Detecta automáticamente la zona UTM correcta basándose en la longitud.
 * Para Andalucía, la mayoría de coordenadas caerán en zona 30 (EPSG:25830).
 * 
 * @param lat - Latitud en grados decimales (+ = Norte)
 * @param lng - Longitud en grados decimales (+ = Este)
 * @returns Coordenada UTM con zona detectada automáticamente
 * 
 * @example
 * // Sevilla
 * const sevilla = toUtm(37.3891, -5.9845);
 * // { x: 235000.xx, y: 4141000.xx, epsg: 'EPSG:25830', zone: 30 }
 * 
 * // Huelva (cerca del límite zona 29)
 * const huelva = toUtm(37.2614, -6.9500);
 * // { x: 669000.xx, y: 4127000.xx, epsg: 'EPSG:25829', zone: 29 }
 */
export function toUtm(lat: number, lng: number): UtmCoordinate {
  // Validar entrada
  if (!isFinite(lat) || !isFinite(lng)) {
    throw new Error(`Coordenadas inválidas: lat=${lat}, lng=${lng}`);
  }
  
  if (lat < -90 || lat > 90) {
    throw new Error(`Latitud fuera de rango [-90, 90]: ${lat}`);
  }
  
  if (lng < -180 || lng > 180) {
    throw new Error(`Longitud fuera de rango [-180, 180]: ${lng}`);
  }

  // Detectar zona UTM
  const epsg = getEpsgForLongitude(lng);
  const zone = getUtmZoneForLongitude(lng);
  
  // Transformar usando proj4
  // proj4 espera [lng, lat] (x, y) no [lat, lng]
  const [x, y] = proj4('EPSG:4326', epsg, [lng, lat]);
  
  return {
    x: Math.round(x * 100) / 100,  // Precisión cm
    y: Math.round(y * 100) / 100,
    epsg,
    zone,
  };
}

/**
 * Convierte coordenadas UTM a un sistema EPSG específico
 * 
 * Similar a toUtm pero permite forzar una zona específica.
 * Útil cuando se sabe de antemano la zona correcta.
 * 
 * @param lat - Latitud en grados decimales
 * @param lng - Longitud en grados decimales
 * @param targetEpsg - Sistema EPSG destino
 * @returns Coordenada UTM en el sistema especificado
 */
export function toUtmInZone(lat: number, lng: number, targetEpsg: EpsgCode): UtmCoordinate {
  if (targetEpsg === 'EPSG:4326') {
    throw new Error('Use toUtm() para conversión a UTM, no EPSG:4326');
  }
  
  const zoneMap: Record<string, UtmZone> = {
    'EPSG:25829': 29,
    'EPSG:25830': 30,
    'EPSG:25831': 31,
  };
  
  const [x, y] = proj4('EPSG:4326', targetEpsg, [lng, lat]);
  
  return {
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    epsg: targetEpsg,
    zone: zoneMap[targetEpsg],
  };
}

/**
 * Convierte coordenadas UTM a WGS84 (lat/lng)
 * 
 * @param x - Coordenada Este (metros)
 * @param y - Coordenada Norte (metros)
 * @param epsg - Código EPSG del sistema UTM origen
 * @returns Coordenada WGS84 en grados decimales
 * 
 * @example
 * // Convertir desde UTM30
 * const wgs = fromUtm(235000, 4141000, 'EPSG:25830');
 * // { lat: 37.xxxx, lng: -5.xxxx }
 * 
 * // También acepta número de EPSG
 * const wgs2 = fromUtm(235000, 4141000, 25830);
 */
export function fromUtm(
  x: number, 
  y: number, 
  epsg: EpsgCode | number
): WgsCoordinate {
  // Normalizar código EPSG
  const epsgCode: string = typeof epsg === 'number' 
    ? `EPSG:${epsg}` 
    : epsg;
  
  // Validar entrada
  if (!isFinite(x) || !isFinite(y)) {
    throw new Error(`Coordenadas UTM inválidas: x=${x}, y=${y}`);
  }
  
  // Validar que el EPSG es conocido
  const validEpsgs = ['EPSG:25829', 'EPSG:25830', 'EPSG:25831', 'EPSG:4326'];
  if (!validEpsgs.includes(epsgCode)) {
    throw new Error(`Código EPSG no soportado: ${epsgCode}. Use: ${validEpsgs.join(', ')}`);
  }
  
  // Si ya es WGS84, devolver directamente
  if (epsgCode === 'EPSG:4326') {
    return { lat: y, lng: x };
  }
  
  // Transformar usando proj4
  const [lng, lat] = proj4(epsgCode, 'EPSG:4326', [x, y]);
  
  return {
    lat: Math.round(lat * 1000000) / 1000000,  // 6 decimales ≈ 0.1m precisión
    lng: Math.round(lng * 1000000) / 1000000,
  };
}

// =============================================================================
// FUNCIONES DE VALIDACIÓN
// =============================================================================

/**
 * Verifica si una coordenada WGS84 está dentro de los límites de Andalucía
 * 
 * @param lat - Latitud en grados decimales
 * @param lng - Longitud en grados decimales
 * @returns true si está dentro del bounding box de Andalucía
 */
export function isInAndalucia(lat: number, lng: number): boolean {
  return (
    lat >= ANDALUCIA_BOUNDS_WGS84.minLat &&
    lat <= ANDALUCIA_BOUNDS_WGS84.maxLat &&
    lng >= ANDALUCIA_BOUNDS_WGS84.minLng &&
    lng <= ANDALUCIA_BOUNDS_WGS84.maxLng
  );
}

/**
 * Verifica si una coordenada UTM30 está dentro de los límites de Andalucía
 * 
 * @param x - Coordenada Este (metros)
 * @param y - Coordenada Norte (metros)
 * @returns true si está dentro del bounding box UTM30 de Andalucía
 */
export function isInAndaluciaUtm(x: number, y: number): boolean {
  return (
    x >= ANDALUCIA_BOUNDS_UTM30.minX &&
    x <= ANDALUCIA_BOUNDS_UTM30.maxX &&
    y >= ANDALUCIA_BOUNDS_UTM30.minY &&
    y <= ANDALUCIA_BOUNDS_UTM30.maxY
  );
}

/**
 * Valida si coordenadas UTM están en un rango plausible para España
 * 
 * @param x - Coordenada Este (metros)
 * @param y - Coordenada Norte (metros)
 * @returns true si las coordenadas son plausibles para España peninsular
 */
export function isValidUtmForSpain(x: number, y: number): boolean {
  // Rango amplio para toda España peninsular
  const validX = x >= 100000 && x <= 900000;
  const validY = y >= 3900000 && y <= 4900000;
  return validX && validY;
}

// =============================================================================
// UTILIDADES ADICIONALES
// =============================================================================

/**
 * Calcula la distancia entre dos puntos UTM (metros)
 * Solo válido si ambos puntos están en la misma zona UTM.
 * 
 * @param p1 - Primer punto UTM
 * @param p2 - Segundo punto UTM
 * @returns Distancia en metros
 */
export function distanceUtm(
  p1: Pick<UtmCoordinate, 'x' | 'y'>, 
  p2: Pick<UtmCoordinate, 'x' | 'y'>
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Normaliza un código EPSG a formato string estándar
 * 
 * @param epsg - Código EPSG (número o string)
 * @returns Código EPSG normalizado (ej: 'EPSG:25830')
 */
export function normalizeEpsg(epsg: string | number): EpsgCode {
  if (typeof epsg === 'number') {
    return `EPSG:${epsg}` as EpsgCode;
  }
  
  // Si ya tiene prefijo EPSG:
  if (epsg.startsWith('EPSG:')) {
    return epsg as EpsgCode;
  }
  
  // Si es solo el número como string
  return `EPSG:${epsg}` as EpsgCode;
}

/**
 * Obtiene el meridiano central de una zona UTM
 * 
 * @param zone - Número de zona UTM
 * @returns Longitud del meridiano central en grados
 */
export function getCentralMeridian(zone: UtmZone): number {
  // Meridiano central = (zona * 6) - 183
  return zone * 6 - 183;
}

/**
 * Exporta las definiciones proj4 como strings para tests y validación
 * Nota: proj4.defs() devuelve transformadores internos, no el string original.
 *       Por eso exportamos PROJ4_DEF_STRINGS directamente.
 */
export const PROJ4_DEFINITIONS = PROJ4_DEF_STRINGS;
